/**
 * Contrato dos contatos do Mailing e dos telefones de cada um.
 *
 * Papel: aqui ficam `TIPOS_TELEFONE`, de onde nasce o enum `tipo_telefone` do
 * Postgres, e os schemas que o formulário e as Functions usam para conferir um
 * contato — `novoContatoMailingSchema` na entrada, `contatoMailingSchema` na
 * saída.
 *
 * Como usar: um contato chega sempre com a lista inteira de telefones, e a
 * gravação substitui o conjunto anterior por ela. Enviar só o telefone que
 * mudou apaga os outros, porque não existe rota que acrescente um número
 * isolado — a lista que chega é a lista que passa a valer.
 *
 * `telefoneOpcionalSchema`, em `comum.ts`, não serve aqui e continua servindo
 * aos demais módulos: ele aceita dez ou onze dígitos sem saber o que o número é.
 * O Mailing guarda o tipo ao lado do número, e a contagem de dígitos que vale
 * depende dele.
 */
import { z } from 'zod';

import { idSchema, textoObrigatorio, textoOpcional } from './comum';

/**
 * Os dois tipos de telefone, na ordem em que o toggle do formulário os mostra.
 *
 * O tipo não é deduzido do número: `(43) 9876-5432` tem oito dígitos depois do
 * DDD e começa com nove, e só quem cadastrou sabe se aquilo é um fixo ou um
 * celular anterior a 2016 que nunca ganhou o nono dígito. Guardar a resposta
 * evita que a tela decida por conta própria e acerte em uns e erre em outros.
 */
export const TIPOS_TELEFONE = ['celular', 'fixo'] as const;

/** Se o número é de celular ou de linha fixa. */
export const tipoTelefoneSchema = z.enum(TIPOS_TELEFONE, {
  message: 'Escolha se o telefone é celular ou fixo.',
});

export type TipoTelefone = z.infer<typeof tipoTelefoneSchema>;

/**
 * O número em si, reduzido a dígitos.
 *
 * O código de país é retirado quando vem junto, porque `5511999999999` e
 * `11999999999` são a mesma linha e guardá-los diferentes faria o mesmo contato
 * aparecer duas vezes numa busca. Quem cola `+55 (11) 98765-4321` de uma
 * conversa precisa ver o número entrar, e não um erro de formato.
 *
 * A contagem aceita aqui é a mais larga das duas — DDD e mais oito ou nove
 * dígitos. O aperto que depende do tipo fica em `telefoneContatoSchema`, que é
 * onde os dois campos existem ao mesmo tempo.
 */
const numeroTelefoneSchema = z
  .string()
  .trim()
  .transform((valor) => {
    const digitos = valor.replace(/\D/g, '');
    return digitos.length > 11 && digitos.startsWith('55') ? digitos.slice(2) : digitos;
  })
  .refine((digitos) => digitos.length === 10 || digitos.length === 11, {
    message: 'Telefone deve ter DDD e mais 8 ou 9 dígitos.',
  });

/**
 * Um telefone de um contato, com o tipo e com quem atende nele.
 *
 * Fixo exige DDD e mais oito dígitos; celular aceita nove, e também oito, para
 * os números anteriores a 2016 que ainda estão na agenda e que ninguém tem como
 * saber se ainda atendem. Fixo com nove dígitos é recusado porque não existe.
 *
 * `falarCom` pertence ao número, e não ao contato: é o nome de quem responde
 * naquela linha — `SAC`, `Priscila`, `Central` —, e um segundo número da mesma
 * empresa costuma ter outro responsável.
 */
export const telefoneContatoSchema = z
  .object({
    tipo: tipoTelefoneSchema,
    numero: numeroTelefoneSchema,
    falarCom: textoOpcional(),
  })
  .refine((telefone) => telefone.tipo === 'celular' || telefone.numero.length === 10, {
    message: 'Telefone fixo tem DDD e mais 8 dígitos.',
    path: ['numero'],
  });

export type TelefoneContato = z.infer<typeof telefoneContatoSchema>;

/**
 * O que o formulário envia para criar ou substituir um contato.
 *
 * `categoria` é texto, e não uma escolha entre valores fixos: as categorias em
 * uso descrevem o que cada fornecedor entrega — `Buffet`, `Velas`, `Tapeceiro`
 * —, e prendê-las a uma lista faria cada contato novo esperar por uma migration.
 * A tela sugere as que já existem através de `GET /api/mailing/categorias`, que
 * é o que impede `Tapeceiro` e `Tapecaria` de conviverem sem que ninguém note.
 *
 * `observacao` aceita bem mais que os demais textos porque é onde a ficha de um
 * fornecedor acaba cabendo por inteiro — nomes, ramais e endereços de várias
 * pessoas dentro do mesmo campo.
 */
export const novoContatoMailingSchema = z.object({
  nome: textoObrigatorio('Nome'),
  categoria: textoObrigatorio('Categoria'),
  email: z
    .string()
    .trim()
    .toLowerCase()
    .nullish()
    .transform((valor) => (valor == null || valor === '' ? null : valor))
    .refine((valor) => valor === null || z.email().safeParse(valor).success, {
      message: 'E-mail inválido.',
    }),
  endereco: textoOpcional(),
  observacao: textoOpcional(2000),
  telefones: z
    .array(telefoneContatoSchema)
    .max(20, { message: 'Um contato aceita no máximo 20 telefones.' })
    .default([])
    .refine(
      (telefones) =>
        new Set(telefones.map((telefone) => telefone.numero)).size === telefones.length,
      { message: 'O mesmo número aparece duas vezes neste contato.' },
    ),
});

export type NovoContatoMailing = z.infer<typeof novoContatoMailingSchema>;

/**
 * Um contato como a API o devolve.
 *
 * Os telefones vêm aninhados e já na ordem de exibição, de modo que a lista não
 * precise de uma segunda requisição para saber o número de cada linha. O
 * primeiro da lista é o principal, e é o único que o cartão da listagem mostra.
 *
 * Os telefones não trazem identificador próprio porque nada os endereça
 * isoladamente: a gravação manda a lista inteira, e a posição é o que os
 * distingue.
 */
export const contatoMailingSchema = novoContatoMailingSchema.extend({
  id: idSchema,
  criadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
  atualizadoEm: z.iso.datetime({ message: 'Instante inválido.' }),
});

export type ContatoMailing = z.infer<typeof contatoMailingSchema>;

/**
 * As categorias já em uso, para o formulário sugerir.
 *
 * Vem da coluna `categoria` dos contatos gravados, e não de uma lista mantida à
 * parte, porque uma lista à parte passaria a discordar do que está no banco na
 * primeira categoria criada por quem cadastra.
 */
export const categoriasMailingSchema = z.array(z.string());
